import { action } from '@reatom/framework'

import { pipe, Result } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { TradeSettingsByNetwork } from '@root/entities/trade-settings/model'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'

import type { TypeHelper } from '@blade/std-lib'
import type {
  TradeSettingsActivePresetUpdatePayload,
  TradeSettingsPresetUpdatePayload,
  TradeSettingsQuickAmountUpdatePayload,
} from '@blade/trade-api-client'

export const fetchDefaultTradeSettingsRequestAction = action(async (ctx) => {
  const { tradeApiClient } = ctx.get(dependenciesAtom)

  const result = await ctx.schedule(() =>
    apiRequest(
      ctx,
      tradeApiClient.services.servicesDefaultTradeSettingsList,
      [],
    ),
  )

  return pipe(
    result,
    Result.map((dto) => TradeSettingsByNetwork.fromDto(dto)),
    Result.mapError(ApiError.fallbackToUnknown),
  )
}, 'fetchDefaultTradeSettingsRequestAction')

export const fetchUserTradeSettingsRequestAction = action(async (ctx) => {
  const { tradeApiClient } = ctx.get(dependenciesAtom)

  const result = await ctx.schedule(() =>
    apiRequest(ctx, tradeApiClient.trade.tradeSettingsList, []),
  )

  return pipe(
    result,
    Result.map((dto) => TradeSettingsByNetwork.fromDto(dto)),
    Result.mapError(ApiError.fallbackToUnknown),
  )
}, 'fetchUserTradeSettingsRequestAction')

type SetActivePresetPayload = TypeHelper.OverrideKeys<
  TradeSettingsActivePresetUpdatePayload,
  { network: Network.Network }
>
export const setActivePresetRequestAction = action(
  async (ctx, { network, preset }: SetActivePresetPayload) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.trade.tradeSettingsActivePresetUpdate, [
        { network: Network.toDto(network), preset: preset },
      ]),
    )

    return pipe(
      result,
      Result.map((dto) => TradeSettingsByNetwork.presetsFromDto(dto, network)),
      Result.mapError(ApiError.fallbackToUnknown),
    )
  },
  'setActivePresetRequestAction',
)

type UpdatePresetPayload = TypeHelper.OverrideKeys<
  TradeSettingsPresetUpdatePayload,
  { network: Network.Network }
>
export const updateTradeSettingsPresetRequestAction = action(
  async (ctx, { network, ...payload }: UpdatePresetPayload) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.trade.tradeSettingsPresetUpdate, [
        {
          network: Network.toDto(network),
          ...payload,
        },
      ]),
    )

    return pipe(
      result,
      Result.map((dto) => TradeSettingsByNetwork.presetsFromDto(dto, network)),
      Result.mapError(ApiError.fallbackToUnknown),
    )
  },
  'updateTradeSettingsPresetRequestAction',
)

export const updateQuickAmountSettingsRequestAction = action(
  async (
    ctx,
    {
      network,
      ...req
    }: TypeHelper.OmitKey<TradeSettingsQuickAmountUpdatePayload, 'network'> & {
      network: Network.Network
    },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.trade.tradeSettingsQuickAmountUpdate, [
        {
          ...req,
          network: Network.toDto(network),
        },
      ]),
    )

    return pipe(
      result,
      Result.map((dto) => TradeSettingsByNetwork.presetsFromDto(dto, network)),
      Result.mapError(ApiError.fallbackToUnknown),
    )
  },
  'updateQuickAmountSettingsRequestAction',
)
